// Poradce zařazení odpadu (rozhodnutí zadavatele 4. 10. 2026; kontrakt tag Asistent, docs/11 §16):
//   POST /zarazeni-odpadu                 fotky + popis → návrhy katalogových čísel (nic nezakládá)
//   POST /zarazeni-odpadu/{id}/vysledek   po přidání položky: kterou z návrhů člověk vzal (měření, paměť u zákazníka)
//   GET  /zarazeni-odpadu/minule          potvrzená zařazení u zákazníka zakázky
//   GET  /zarazeni-odpadu                 přehled dotazů (vlastní; s evidence.cist všech) i s fotkami (05/1982)
import type { Schema } from '@ecoser/shared';
import { useQuery } from '@tanstack/react-query';
import { api, urlEndpointu } from './klient';

export type ZarazeniOdpadu = Schema<'ZarazeniOdpadu'>;
export type NavrhZarazeni = Schema<'ZarazeniOdpaduNavrh'>;
export type ZarazeniOdpaduVstup = Schema<'ZarazeniOdpaduVstup'>;
export type MinuleZarazeni = Schema<'ZarazeniOdpaduMinule'>;
export type ZarazeniOdpaduRadek = Schema<'ZarazeniOdpaduRadek'>;
export type ZarazeniOdpaduSeznam = Schema<'ZarazeniOdpaduSeznam'>;
export type FotkaZarazeni = Schema<'ZarazeniOdpaduFotka'>;

/** Právo poradce (08 §3.18). */
export const AKCE_ZARADIT = 'odpad.zaradit';
/** Dotazy všech v přehledu (A J D K). */
export const AKCE_VSECHNY_DOTAZY = 'evidence.cist';
/** Nejvýš fotek v jednom dotazu (kontrakt `ZarazeniOdpaduVstup`). */
export const MAX_FOTEK = 3;

export const KLIC_ZARAZENI = ['zarazeni-odpadu'] as const;

/** Fotka dotazu (GET /soubory/{id}/obsah — otevře ji, kdo dotaz vidí); `nahled` = zmenšenina 480 px. */
export const urlFotkyZarazeni = (souborId: string, nahled = false) =>
  urlEndpointu(`/soubory/${encodeURIComponent(souborId)}/obsah${nahled ? '?nahled=1' : ''}`);

export const navrhnoutZarazeni = (telo: ZarazeniOdpaduVstup) =>
  api.post<ZarazeniOdpadu>('/zarazeni-odpadu', telo);

export const zapsatVysledekZarazeni = (
  id: string,
  telo: ({ zakazka_polozka_id: string } | { nabidka_polozka_id: string }) & { poradi?: number },
) => api.post<ZarazeniOdpadu>(`/zarazeni-odpadu/${id}/vysledek`, telo);

/** „Minule u zákazníka“ — jen u zakázky. */
export function useMinulaZarazeni(zakazkaId: string | undefined) {
  return useQuery({
    queryKey: [...KLIC_ZARAZENI, 'minule', zakazkaId],
    queryFn: () =>
      api.get<{ polozky: MinuleZarazeni[] }>(
        `/zarazeni-odpadu/minule?${new URLSearchParams({ zakazka_id: zakazkaId ?? '' }).toString()}`,
      ),
    enabled: Boolean(zakazkaId),
    staleTime: 60_000,
  });
}

/** Přehled dotazů poradce (Asistent › Zařazení odpadů). */
export function useZarazeniOdpadu(filtr: { jenPouzite: boolean; strana: number }) {
  return useQuery({
    queryKey: [...KLIC_ZARAZENI, 'seznam', filtr],
    queryFn: () => {
      const q = new URLSearchParams({ strana: String(filtr.strana), velikost: '50' });
      if (filtr.jenPouzite) q.set('jen_pouzite', 'true');
      return api.get<ZarazeniOdpaduSeznam>(`/zarazeni-odpadu?${q.toString()}`);
    },
  });
}
